// Global overlay host (one per page): toasts, the S6 card sheet, the request/email modal.
// Pages call: toast(msg, kind) · openCard(cardId, opts) · requestCards({items, title}) -> Promise
import { mount, bindGlobalKeys } from './singles-hub--core--app.js?v=c7c57de095';
import { html, attr } from './singles-hub--core--html.js?v=c7c57de095';
import { api } from './singles-hub--api.js?v=c7c57de095';
import { cart } from './singles-hub--cart.js?v=c7c57de095';
import { track } from './singles-hub--analytics.js?v=c7c57de095';
import { config, route } from './singles-hub--config.js?v=c7c57de095';
import { money, toastMs, pretty, copies } from './singles-hub--format.js?v=c7c57de095';
import { cardArt, dialog, legalPill, stockPill, stepper, overlayClose, settleImages, busy } from './singles-hub--components--ui.js?v=c7c57de095';
import { normCard, availableListings, priceOf, picksFor } from './singles-hub--stock.js?v=c7c57de095';

let host = null;
let tid = 0;
const timers = {};

const LSK = { email: 'mcc.req.email', token: 'mcc.req.token' };
const lsGet = (k) => { try { return localStorage.getItem(k); } catch (e) { return null; } };
const lsSet = (k, v) => { try { localStorage.setItem(k, v); } catch (e) { /* private mode */ } };

export function requestIdentity() {
  const c = config().customer;
  if (c) return { customer_id: c.id, email: c.email, logged_in: true };
  const token = lsGet(LSK.token), email = lsGet(LSK.email);
  return token ? { token, email, logged_in: false } : null;
}

// Toasts (Q18): info/ok auto-dismiss after 4s (+1s per 10 words over 10), stock warnings after 7s, errors stay until
// dismissed. At most 2 on screen. One fixed position (bottom-left on desktop, clear of the builder's CTA column;
// top on phones, clear of the sticky basket bar). opts.action = {label, fn} renders a real button (Undo).
const actionsById = {};
export function toast(msg, kind = 'info', opts = {}) {
  ensureHost();
  const id = ++tid;
  if (opts.action) actionsById[id] = opts.action;
  host.setState((s) => {
    const keep = s.toasts.filter((t) => t.msg !== msg).concat([{ id, msg, kind, action: opts.action ? opts.action.label : null }]);
    keep.slice(0, -2).forEach((t) => { clearTimeout(timers[t.id]); delete actionsById[t.id]; });
    return { toasts: keep.slice(-2) };
  });
  const ms = opts.ms || (kind === 'error' ? 0 : kind === 'stock' ? 7000 : Math.max(4000, toastMs(msg) + (opts.action ? 4000 : 1000)));
  if (ms) timers[id] = setTimeout(() => dismiss(id), ms);
  return id;
}
function dismiss(id) { clearTimeout(timers[id]); delete actionsById[id]; host.setState((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })); }

// Requested state (Q20): remembered per card (+ printing) on this device so Request buttons turn into "Requested ✓"
const REQ_KEY = 'mcc.req.cards';
const reqSet = () => { try { return new Set(JSON.parse(localStorage.getItem(REQ_KEY) || '[]')); } catch (e) { return new Set(); } };
export function isRequested(cardId, printingId) { const s = reqSet(); return s.has(cardId + '|' + (printingId || '*')) || s.has(cardId + '|*'); }
function markRequested(items) { const s = reqSet(); items.forEach((i) => s.add(i.card_id + '|' + (i.printing_id || '*'))); try { localStorage.setItem(REQ_KEY, JSON.stringify([...s])); } catch (e) { /* ignore */ } }
export function unmarkRequested(cardId, printingId) { const s = reqSet(); s.delete(cardId + '|' + (printingId || '*')); try { localStorage.setItem(REQ_KEY, JSON.stringify([...s])); } catch (e) { /* ignore */ } }
export function dismissTopToast() { if (host && host.state.toasts.length) dismiss(host.state.toasts[host.state.toasts.length - 1].id); }

export async function openCard(cardId, opts = {}) {
  ensureHost();
  host.setState({ sheet: { id: cardId, loading: true, data: null, sel: opts.printingId || null, vsel: opts.variantId || null, qty: 1, onDeck: opts.onAddToDeck || null, deckName: opts.deckName || null } });
  const still = () => host.state.sheet && host.state.sheet.id === cardId;
  try {
    const d = normCard(await api.card(cardId));
    if (!still()) return;
    const prefer = opts.printingId || (d.cheapest && d.cheapest.printing_id) || d.handle_printing_id || (d.printings[0] && d.printings[0].printing_id);
    // open on the cheapest printing with copies AVAILABLE (store stock minus the basket), else the listed/requested one
    const inStock = d.printings.filter((p) => p.copies_available > 0).sort((a, b) => a.price_from - b.price_from);
    const sel = opts.printingId && d.printings.some((p) => p.printing_id === opts.printingId && p.copies_available > 0) ? opts.printingId : (inStock[0] ? inStock[0].printing_id : prefer);
    host.setState((s) => ({ sheet: Object.assign({}, s.sheet, { loading: false, data: d, sel }) }));
  } catch (e) {
    if (!still()) return;
    host.setState((s) => ({ sheet: Object.assign({}, s.sheet, { loading: false, error: e.offline ? 'Card details are offline right now.' : 'We couldn’t load this card.' }) }));
  }
}

// items: [{card_id, qty, name, deck_id?, deck_name?, printing_id?}]
export function requestCards({ items, title }) {
  ensureHost();
  const id = requestIdentity();
  const n = items.reduce((a, i) => a + (i.qty || 1), 0);
  if (id && (id.logged_in || id.email)) return submitRequest(items, id, n);
  return new Promise((resolve) => {
    host.setState({ req: { items, title: title || `Request ${n} card${n === 1 ? '' : 's'}`, n, email: lsGet(LSK.email) || '', error: '', resolve } });
  });
}

async function submitRequest(items, id, n) {
  try {
    const r = await api.createRequest({ email: id.email || undefined, customer_id: id.customer_id || undefined, token: id.token || undefined, items: items.map((i) => ({ card_id: i.card_id, qty: i.qty || 1, deck_id: i.deck_id || undefined, deck_name: i.deck_name || undefined, printing_id: i.printing_id || undefined })) });
    if (r && r.token && !id.logged_in) lsSet(LSK.token, r.token);
    markRequested(items);
    track('hub_card_requested', { lines: items.length, cards: n, deck_name: (items[0] || {}).deck_name });
    toast(n === 1 ? 'Requested. It’s on your wanted list.' : `Requested ${n} cards. They’re on your wanted list.`, 'ok');
    try { window.dispatchEvent(new CustomEvent('mcc:requested', { detail: { items } })); } catch (e) { /* ignore */ }
    return { ok: true, n, items };
  } catch (e) {
    toast(e.offline ? 'Requests are paused while our deck tools are offline. Please try again shortly.' : 'We couldn’t save that request. Please try again.', 'error');
    return { ok: false };
  }
}

function ensureHost() {
  if (host) return host;
  const el = document.createElement('div');
  el.className = 'mcc mcc-host';
  el.setAttribute('data-mcc-host', '');
  document.body.appendChild(el);
  bindGlobalKeys(() => dismissTopToast());
  host = mount(el, { state: { toasts: [], sheet: null, req: null }, view, actions, afterRender: (c) => settleImages(c.root) });
  return host;
}
export const initHost = ensureHost;

// ------------------------------------------------------------ views
function view(s) {
  return html`
    ${s.sheet ? sheetView(s.sheet) : ''}
    ${s.req ? reqView(s.req) : ''}
    <div class="mcc-toasts" aria-live="polite" role="status">
      ${s.toasts.map((t) => html`<div class="mcc-toast mcc-toast--${t.kind}" data-key="t${t.id}" ${attr('role', t.kind === 'error', 'alert')}><p>${t.msg}</p>${t.action ? html`<button type="button" class="mcc-toast__act" data-click="toastAct" data-id="${t.id}">${t.action}</button>` : ''}<button type="button" data-click="dismiss" data-id="${t.id}" aria-label="Dismiss">×</button></div>`)}
    </div>`;
}

function reqView(r) {
  const cfg = config();
  return dialog({
    id: 'mcc-host-req', size: 'sm', title: r.title,
    body: html`<form data-submit="reqSubmit" id="mcc-req-form" novalidate>
      <p class="mcc-small">Free. ${r.n === 1 ? 'It goes' : 'They go'} on your wanted list and our buying list. Requests last 90 days.</p>
      <label class="mcc-small" for="mcc-req-email" style="display:block;margin:14px 0 6px;font-weight:600">Email address</label>
      <input class="mcc-input" id="mcc-req-email" type="email" autocomplete="email" required value="${r.email}" data-input="reqEmail" data-autofocus aria-describedby="mcc-req-help${r.error ? ' mcc-req-err' : ''}" ${attr('aria-invalid', r.error, 'true')}>
      ${r.error ? html`<p id="mcc-req-err" class="mcc-small" style="color:var(--mcc-bad);margin-top:6px">${r.error}</p>` : ''}
      <p id="mcc-req-help" class="mcc-xs mcc-muted" style="margin-top:10px">We only use your email for your requests. <a href="${cfg.privacyUrl}" class="mcc-link">Privacy policy</a>. Have an account? <a class="mcc-link" href="${cfg.loginUrl}">Log in</a> and we’ll save requests to it.</p>
    </form>`,
    foot: html`<div class="mcc-btns"><button type="button" class="mcc-btn mcc-btn--secondary" data-click="reqCancel">Cancel</button><button type="submit" form="mcc-req-form" class="mcc-btn">Request ${r.n} card${r.n === 1 ? '' : 's'}</button></div>`,
  });
}

function sheetView(sh) {
  if (sh.loading || !sh.data) {
    return dialog({ id: 'mcc-host-sheet', side: true, title: 'Card details', body: sh.error ? html`<p class="mcc-small">${sh.error}</p>` : html`<div class="mcc-skel" style="height:220px"></div><div class="mcc-skel" style="height:16px;margin-top:16px"></div><div class="mcc-skel" style="height:16px;margin-top:8px;width:60%"></div>` });
  }
  const d = sh.data;
  const sel = d.printings.find((p) => p.printing_id === sh.sel) || d.printings[0];
  const singles = sel ? sel.listings.filter((l) => !l.is_playset) : [];
  // Operator 2026-10-12: Budew PRE 4 has Normal, Reverse, Poké Ball and Master Ball listings; show and sell each finish.
  const fin = singles.find((l) => String(l.variant_id) === String(sh.vsel)) || null;
  const inS = sel ? availableListings(d, sel.printing_id).filter((l) => !fin || String(l.variant_id) === String(fin.variant_id)) : [];
  const avail = fin ? fin.copies_available : sel ? sel.copies_available : 0;
  const inBasket = fin ? fin.in_basket : sel ? sel.in_basket : 0;
  const selLabel = sel ? sel.label + (fin ? ' · ' + fin.finish : '') : '';
  const qty = Math.max(1, Math.min(sh.qty, Math.max(1, avail)));
  const price = priceOf(inS, qty);
  const elsewhere = d.copies_available - avail; // other printings listed above, available now
  const reqd = isRequested(d.card_id, sel && sel.printing_id);
  const isPoke = d.supertype === 'Pokémon';
  const regLine = d.basic_energy ? 'Basic Energy · always legal' : sel && sel.reg ? (['H', 'I', 'J'].includes(sel.reg) ? `Regulation mark ${sel.reg}` : (d.legal ? legalReprint(d) : `Rotated (Reg. ${sel.reg})`)) : '';
  const body = html`
    <div class="mcc-sheet-card">
      <div>${cardArt((fin && fin.image_url) || (sel && sel.image_url), { name: d.name, code: selLabel, w: 300, eager: true })}</div>
      <div class="mcc-stack">
        <h3 class="mcc-h2">${pretty(d.name)}</h3>
        <p class="mcc-small mcc-muted">${d.type_line || d.type}</p>
        <div class="mcc-row">${legalPill(d.legal)}${regLine ? html`<span class="mcc-pill mcc-pill--line">${regLine}</span>` : ''}</div>
        <p class="mcc-xs mcc-muted">${d.usage_text}</p>
      </div>
    </div>
    ${isPoke && (d.hp || d.attacks.length) ? html`<div class="mcc-stat">
      <div class="mcc-stat__row"><span>${d.stage || d.subtype}</span>${d.hp ? html`<b>HP ${d.hp}</b>` : ''}</div>
      ${d.ability ? html`<div><b>Ability · ${d.ability.name}</b><p class="mcc-muted">${d.ability.text}</p></div>` : ''}
      ${d.attacks.map((a) => html`<div><div class="mcc-stat__row"><span><span class="mcc-cost">${a.cost || 'free'}</span><b>${a.name}</b></span><b>${a.damage || ''}</b></div>${a.text ? html`<p class="mcc-muted">${a.text}</p>` : ''}</div>`)}
      <div class="mcc-stat__wrr"><div>Weakness<b>${d.weakness || '—'}</b></div><div>Resistance<b>${d.resistance || '—'}</b></div><div>Retreat<b>${d.retreat == null ? '—' : d.retreat}</b></div></div>
    </div>` : ''}
    ${!isPoke && d.rules_text ? html`<p class="mcc-small" style="margin-top:12px">${d.rules_text}</p>` : ''}
    ${d.competitive_note ? html`<p class="mcc-small" style="margin-top:12px"><b>Competitive note.</b> ${d.competitive_note}</p>` : ''}
    <h4 class="mcc-eyebrow" style="color:var(--mcc-ink2);margin:20px 0 6px">All printings</h4>
    <div>${d.printings.map((p) => html`<button type="button" class="mcc-printrow" data-click="sheetSel" data-pid="${p.printing_id}" aria-pressed="${p.printing_id === (sel && sel.printing_id) ? 'true' : 'false'}">
      <span><b class="mcc-small">${p.label}${p.reg ? ' · Reg. ' + p.reg : ''}</b><span class="mcc-xs mcc-muted" style="display:block">${p.rarity}${p.illustrative ? ' · sample data' : ''}</span></span>
      ${stockPill(p.copies_available, p.in_basket)}<b class="mcc-num">${p.price_from != null ? money(p.price_from) : '—'}</b></button>
      ${p === sel && singles.length > 1 ? finishRows(singles, fin) : ''}`)}</div>
    ${d.used_in_archetypes && d.used_in_archetypes.length ? html`<h4 class="mcc-eyebrow" style="color:var(--mcc-ink2);margin:20px 0 6px">Used in</h4>
      <ul>${d.used_in_archetypes.map((a) => html`<li class="mcc-row" style="justify-content:space-between;padding:6px 0;border-top:1px solid var(--mcc-line2)"><a class="mcc-link" href="${route('archetype', { slug: a.slug })}">${pretty(a.name)}</a><span class="mcc-small mcc-muted">usually ${a.typical} · in ${a.lists} of ${a.of} list${a.of === 1 ? '' : 's'}</span></li>`)}</ul>` : ''}
    <p style="margin-top:16px">${d.product_url || (singles[0] && singles[0].handle) ? html`<a class="mcc-link" href="${d.product_url || route('product', { handle: singles[0].handle })}">Full product page →</a>` : ''}</p>`;
  const foot = html`
    <p class="mcc-xs mcc-muted" style="margin-bottom:8px">Selected: ${selLabel} · ${sel ? sel.rarity : ''} · ${avail ? copies(avail) + ' available' : inBasket ? `${inBasket} already in your basket` : 'sold out'}${avail && inBasket ? ` · ${inBasket} already in your basket` : ''}</p>
    ${avail ? html`<div class="mcc-btngroup"><div class="mcc-btnrow">${stepper({ value: qty, min: 1, max: avail, minus: 'sheetMinus', plus: 'sheetPlus', label: 'Quantity' })}<button type="button" class="mcc-btn" data-click="sheetAdd">Add ${qty} · ${money(price)}</button></div>
      <button type="button" class="mcc-btn mcc-btn--secondary mcc-btn--block" data-click="sheetSet">${playsetLabel(avail, inS)}</button>
      ${sh.onDeck ? html`<button type="button" class="mcc-btn mcc-btn--outline mcc-btn--block" data-click="sheetDeck">Add 1 to ${sh.deckName || 'my deck'}</button>` : ''}</div>`
    : html`<p class="mcc-small" style="color:var(--mcc-muted);margin-bottom:8px">${inBasket ? `You have all ${inBasket} we stock of this printing in your basket` : 'This printing is sold out'}${elsewhere > 0 ? ` · ${copies(elsewhere)} of another printing available above` : ''}.</p>${reqd ? html`<button type="button" class="mcc-btn mcc-btn--ok mcc-btn--block" disabled aria-disabled="true">Requested ✓</button>` : html`<button type="button" class="mcc-btn mcc-btn--outline mcc-btn--block" data-click="sheetReq">Request it</button>`}`}`;
  return dialog({ id: 'mcc-host-sheet', side: true, title: 'Card details', body, foot });
}
// The finishes of the selected printing: "Any finish" (cheapest first) or one exact listing.
function finishRows(singles, fin) {
  const sorted = singles.slice().sort((a, b) => a.price - b.price);
  return html`<div class="mcc-finrows" role="group" aria-label="Finish">
    <button type="button" class="mcc-finrow" data-click="sheetFin" data-vid="" aria-pressed="${fin ? 'false' : 'true'}"><span class="mcc-small">Any finish · cheapest first</span></button>
    ${sorted.map((l) => html`<button type="button" class="mcc-finrow" data-click="sheetFin" data-vid="${l.variant_id}" aria-pressed="${fin && String(fin.variant_id) === String(l.variant_id) ? 'true' : 'false'}"><span class="mcc-small">${l.finish || 'Normal'}</span>${stockPill(l.copies_available, l.in_basket)}<b class="mcc-num">${money(l.price)}</b></button>`)}
  </div>`;
}
function legalReprint(d) {
  const lp = d.printings.find((p) => ['H', 'I', 'J'].includes(p.reg));
  return lp ? `Legal: reprinted in ${lp.set_code} (Reg. ${lp.reg})` : 'Standard legal';
}
// Playset label from what can really be fulfilled (Q12): "Add playset (4) · £x" only when 4 are available.
// mixed = the copies come from more than one printing (the shown printing has fewer left), so the label says so.
export function playsetLabel(avail, listings, mixed = false) {
  if (mixed) return `${avail >= 4 ? 'Add 4' : 'Add all ' + avail}, mixed printings · ${money(priceOf(listings, Math.min(4, avail)))}`;
  return avail >= 4 ? `Add playset (4) · ${money(priceOf(listings, 4))}` : `Add all ${avail} · ${money(priceOf(listings, avail))}`;
}

// ------------------------------------------------------------ actions
const actions = {
  dismiss: (el) => dismiss(+el.dataset.id),
  toastAct: (el) => { const a = actionsById[+el.dataset.id]; dismiss(+el.dataset.id); if (a && a.fn) a.fn(); },
  overlayClick: (el, e) => overlayClose(el, e, (id) => actions.closeDialog({ dataset: { dialogId: id }, getAttribute: () => id })),
  closeDialog: (el) => {
    const id = el.getAttribute ? el.getAttribute('data-dialog-id') : el.dataset.dialogId;
    if (id === 'mcc-host-sheet') host.setState({ sheet: null });
    if (id === 'mcc-host-req') actions.reqCancel();
  },
  sheetSel: (el) => host.setState((s) => ({ sheet: Object.assign({}, s.sheet, { sel: el.dataset.pid, vsel: null, qty: 1 }) })),
  sheetFin: (el) => host.setState((s) => ({ sheet: Object.assign({}, s.sheet, { vsel: el.dataset.vid || null, qty: 1 }) })),
  sheetMinus: () => host.setState((s) => ({ sheet: Object.assign({}, s.sheet, { qty: Math.max(1, s.sheet.qty - 1) }) })),
  sheetPlus: () => host.setState((s) => ({ sheet: Object.assign({}, s.sheet, { qty: s.sheet.qty + 1 }) })),
  sheetAdd: (el) => busy(el, () => addFromSheet(host.state.sheet.qty)),
  sheetSet: (el) => busy(el, () => addFromSheet(4)),
  sheetDeck: () => { const sh = host.state.sheet; if (sh.onDeck) sh.onDeck(sh.data, sh.sel); host.setState({ sheet: null }); },
  sheetReq: async () => { const d = host.state.sheet.data; await requestCards({ items: [{ card_id: d.card_id, qty: 1, name: d.name, printing_id: host.state.sheet.sel }], title: `Request ${pretty(d.name)}` }); host.schedule(); },
  reqEmail: (el) => { host.state.req.email = el.value; },
  reqCancel: () => { const r = host.state.req; host.setState({ req: null }); if (r && r.resolve) r.resolve({ ok: false, cancelled: true }); },
  reqSubmit: async () => {
    const r = host.state.req;
    const email = (r.email || '').trim();
    if (!email) { host.setState({ req: Object.assign({}, r, { error: 'Add an email so we can tell you when it lands.' }) }); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) { host.setState({ req: Object.assign({}, r, { error: 'That doesn’t look like an email address. Check it, like name@example.com.' }) }); return; }
    lsSet(LSK.email, email);
    host.setState({ req: null });
    const out = await submitRequest(r.items, { email, token: lsGet(LSK.token) || undefined, logged_in: false }, r.n);
    if (r.resolve) r.resolve(out);
  },
};

async function addFromSheet(n) {
  const sh = host.state.sheet; const d = sh.data;
  const sel = d.printings.find((p) => p.printing_id === sh.sel) || d.printings[0];
  const ls = availableListings(d, sel.printing_id).filter((l) => !sh.vsel || String(l.variant_id) === String(sh.vsel));
  const fin = sh.vsel && ls[0] ? ' · ' + ls[0].finish : '';
  const items = picksFor(ls, n);
  try {
    const r = await cart.addItems(items);
    const got = r.added.reduce((a, x) => a + x.qty, 0);
    if (!got) toast('You already have all we stock in your basket.', 'stock');
    else toast(`Added ${got}× ${pretty(d.name)} (${sel.label}${fin})${got < n ? ' · that’s all we have' : ''}.`, got < n ? 'stock' : 'ok');
    // re-read the card with the new basket so the sheet shows what is left
    if (host.state.sheet && host.state.sheet.id === sh.id) openCard(sh.id, { printingId: sh.sel, variantId: sh.vsel, onAddToDeck: sh.onDeck, deckName: sh.deckName });
  } catch (e) { toast('We couldn’t add that to your basket. Please try again.', 'error'); }
}
